import { useState, useEffect, useCallback, useMemo } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, FileDown, CheckCircle, XCircle, Trash2, Loader2, Eye, Pencil, Search, FileUp, MoreHorizontal, DollarSign } from 'lucide-react';
import { toast } from 'sonner';
import api, { formatCurrency, formatDate, CURRENCY_LIST } from '@/lib/api';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const EMPTY_ITEM = { description: '', hsn_sac_code: '', quantity: 1, unit_price: 0, tax_rate: 0, tax_amount: 0, amount: 0 };
const EMPTY_FORM = { vendor_name: '', vendor_email: '', billing_address: '', invoice_date: new Date().toISOString().split('T')[0], credit_terms: 0, amount: 0, subtotal: 0, tax_amount: 0, currency: 'USD', description: '', due_date: '', bank_details: '', notes: '', items: [{ ...EMPTY_ITEM }] };
const CREDIT_TERMS = [0, 15, 30, 45, 60, 90];
const STATUS_OPTIONS = [
  { value: 'all', label: 'All Statuses' },
  { value: 'pending', label: 'Pending' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'paid', label: 'Paid' },
];

export default function AccountsPayablePage() {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [saving, setSaving] = useState(false);
  const [editId, setEditId] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewNumber, setPreviewNumber] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const fetchInvoices = useCallback(async () => {
    try {
      const { data } = await api.get('/invoices/payable');
      setInvoices(data);
    } catch { toast.error('Failed to load invoices'); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchInvoices(); }, [fetchInvoices]);

  const filteredInvoices = useMemo(() => {
    let list = invoices;
    if (statusFilter !== 'all') {
      list = list.filter(inv => inv.status === statusFilter);
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      list = list.filter(inv =>
        (inv.invoice_number || '').toLowerCase().includes(q) ||
        (inv.vendor_name || '').toLowerCase().includes(q) ||
        (inv.po_reference || '').toLowerCase().includes(q) ||
        (inv.vendor_invoice_number || '').toLowerCase().includes(q)
      );
    }
    return list;
  }, [invoices, searchQuery, statusFilter]);

  const recalcTotals = (items) => {
    const subtotal = items.reduce((s, i) => s + (Number(i.quantity) * Number(i.unit_price)), 0);
    const taxTotal = items.reduce((s, i) => s + Number(i.tax_amount), 0);
    return { subtotal, tax_amount: taxTotal, amount: subtotal + taxTotal };
  };

  const updateItem = (idx, field, value) => {
    const items = [...form.items];
    items[idx] = { ...items[idx], [field]: value };
    if (['quantity', 'unit_price', 'tax_rate'].includes(field)) {
      const lineTotal = Number(items[idx].quantity) * Number(items[idx].unit_price);
      items[idx].tax_amount = lineTotal * (Number(items[idx].tax_rate) / 100);
      items[idx].amount = lineTotal + items[idx].tax_amount;
    }
    const totals = recalcTotals(items);
    setForm({ ...form, items, ...totals });
  };

  const addItem = () => setForm({ ...form, items: [...form.items, { ...EMPTY_ITEM }] });
  const removeItem = (idx) => {
    const items = form.items.filter((_, i) => i !== idx);
    const totals = recalcTotals(items);
    setForm({ ...form, items, ...totals });
  };

  const handleCreditTerms = (days) => {
    const d = Number(days);
    let due = form.due_date;
    if (d > 0 && form.invoice_date) {
      const inv = new Date(form.invoice_date);
      inv.setDate(inv.getDate() + d);
      due = inv.toISOString().split('T')[0];
    }
    setForm({ ...form, credit_terms: d, due_date: due });
  };

  const openCreateDialog = () => {
    setEditId(null);
    setForm({ ...EMPTY_FORM });
    setDialogOpen(true);
  };

  const openEditDialog = (inv) => {
    setEditId(inv.id);
    setForm({
      vendor_name: inv.vendor_name || '', vendor_email: inv.vendor_email || '',
      billing_address: inv.billing_address || '', invoice_date: inv.invoice_date || '',
      credit_terms: inv.credit_terms || 0, amount: inv.amount || 0, subtotal: inv.subtotal || 0,
      tax_amount: inv.tax_amount || 0, currency: inv.currency || 'USD', description: inv.description || '',
      due_date: inv.due_date || '', bank_details: inv.bank_details || '', notes: inv.notes || '',
      items: (inv.items && inv.items.length > 0) ? inv.items : [{ ...EMPTY_ITEM }]
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.vendor_name) { toast.error('Vendor name is required'); return; }
    setSaving(true);
    try {
      if (editId) {
        await api.put(`/invoices/payable/${editId}`, form);
        toast.success('Invoice updated');
      } else {
        await api.post('/invoices/payable', form);
        toast.success('Invoice created');
      }
      setDialogOpen(false);
      setEditId(null);
      setForm({ ...EMPTY_FORM });
      fetchInvoices();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save invoice'); }
    setSaving(false);
  };

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/invoices/payable/${id}/status`, { status });
      toast.success(`Invoice ${status}`);
      fetchInvoices();
    } catch { toast.error('Failed to update status'); }
  };

  const deleteInvoice = async (id) => {
    try {
      await api.delete(`/invoices/payable/${id}`);
      toast.success('Invoice deleted');
      fetchInvoices();
    } catch { toast.error('Failed to delete invoice'); }
  };

  const downloadPdf = async (id, number) => {
    try {
      const response = await fetch(`${API_URL}/api/invoices/payable/${id}/pdf`, { credentials: 'include' });
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = `${number}.pdf`; a.click();
      window.URL.revokeObjectURL(url);
    } catch { toast.error('Failed to download PDF'); }
  };

  const openPreview = async (id, number) => {
    try {
      const resp = await fetch(`${API_URL}/api/invoices/payable/${id}/pdf`, { credentials: 'include' });
      if (!resp.ok) throw new Error();
      const blob = await resp.blob();
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      setPreviewUrl(URL.createObjectURL(blob));
      setPreviewNumber(number);
      setPreviewOpen(true);
    } catch { toast.error('Failed to load preview'); }
  };

  const downloadVendorFile = async (fileId, filename) => {
    try {
      const resp = await fetch(`${API_URL}/api/vendor-upload-file/${fileId}`, { credentials: 'include' });
      if (!resp.ok) throw new Error();
      const blob = await resp.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = filename; a.click();
      window.URL.revokeObjectURL(url);
    } catch { toast.error('Failed to download file'); }
  };

  const previewVendorFile = async (fileId, filename) => {
    try {
      const resp = await fetch(`${API_URL}/api/vendor-upload-file/${fileId}`, { credentials: 'include' });
      if (!resp.ok) throw new Error();
      const blob = await resp.blob();
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      setPreviewUrl(URL.createObjectURL(blob));
      setPreviewNumber(filename);
      setPreviewOpen(true);
    } catch { toast.error('Failed to load file preview'); }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="accounts-payable-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>Accounts Payable</h1>
          <p className="text-sm text-muted-foreground mt-1">Manage vendor invoices received</p>
        </div>
        <Button onClick={openCreateDialog} className="bg-blue-600 hover:bg-blue-700 text-white gap-2" data-testid="create-payable-button">
          <Plus size={16} /> New Invoice
        </Button>
      </div>

      {/* Search & Filter Bar */}
      <div className="flex items-center gap-3" data-testid="ap-filter-bar">
        <div className="relative flex-1 max-w-sm">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
            placeholder="Search by invoice #, vendor, or PO ref..."
            className="pl-9 bg-[hsl(var(--surface-input))] border-input text-foreground h-9"
            data-testid="ap-search-input"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[160px] bg-[hsl(var(--surface-input))] border-input text-foreground h-9" data-testid="ap-status-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-popover border-border">
            {STATUS_OPTIONS.map(s => <SelectItem key={s.value} value={s.value} className="text-foreground">{s.label}</SelectItem>)}
          </SelectContent>
        </Select>
        {(searchQuery || statusFilter !== 'all') && (
          <span className="text-xs text-muted-foreground" data-testid="ap-result-count">{filteredInvoices.length} result{filteredInvoices.length !== 1 ? 's' : ''}</span>
        )}
      </div>

      <Card className="bg-card border-border">
        <CardContent className="p-0">
          <Table className="data-table">
            <TableHeader>
              <TableRow className="border-border hover:bg-transparent">
                <TableHead className="text-muted-foreground">Invoice #</TableHead>
                <TableHead className="text-muted-foreground">Vendor</TableHead>
                <TableHead className="text-muted-foreground">PO Ref</TableHead>
                <TableHead className="text-muted-foreground">Basic Amt</TableHead>
                <TableHead className="text-muted-foreground">Tax Amt</TableHead>
                <TableHead className="text-muted-foreground">Total Amt</TableHead>
                <TableHead className="text-muted-foreground">Due Date</TableHead>
                <TableHead className="text-muted-foreground">Source</TableHead>
                <TableHead className="text-muted-foreground">Status</TableHead>
                <TableHead className="text-muted-foreground text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow className="border-border"><TableCell colSpan={10} className="text-center text-muted-foreground py-8"><Loader2 className="animate-spin inline" size={16} /> Loading...</TableCell></TableRow>
              ) : filteredInvoices.length === 0 ? (
                <TableRow className="border-border"><TableCell colSpan={10} className="text-center text-muted-foreground py-8">{invoices.length === 0 ? 'No vendor invoices yet. Create one to get started.' : 'No invoices match your search.'}</TableCell></TableRow>
              ) : filteredInvoices.map(inv => (
                <TableRow key={inv.id} className="border-border hover:bg-muted/50" data-testid={`payable-row-${inv.id}`}>
                  <TableCell className="text-card-foreground/80 font-mono text-xs">
                    <div>{inv.invoice_number}</div>
                    {inv.vendor_invoice_number && <div className="text-[10px] text-muted-foreground">Vendor: {inv.vendor_invoice_number}</div>}
                  </TableCell>
                  <TableCell className="text-foreground">{inv.vendor_name}</TableCell>
                  <TableCell className="text-blue-400 font-mono text-xs">{inv.po_reference || '-'}</TableCell>
                  <TableCell className="text-foreground font-mono text-sm">{inv.subtotal ? formatCurrency(inv.subtotal, inv.currency) : '-'}</TableCell>
                  <TableCell className="text-muted-foreground font-mono text-sm">{inv.tax_amount ? formatCurrency(inv.tax_amount, inv.currency) : '-'}</TableCell>
                  <TableCell className="text-foreground font-mono font-medium">{formatCurrency(inv.amount, inv.currency)}</TableCell>
                  <TableCell className="text-muted-foreground text-sm">{formatDate(inv.due_date || inv.invoice_date)}</TableCell>
                  <TableCell>
                    {inv.created_by === 'vendor-upload' ? (
                      <Badge variant="outline" className="text-xs text-blue-400 border-blue-400/30 gap-1"><FileUp size={10} /> Uploaded</Badge>
                    ) : (
                      <Badge variant="outline" className="text-xs text-muted-foreground">Manual</Badge>
                    )}
                  </TableCell>
                  <TableCell><Badge variant="outline" className={`status-${inv.status} text-xs capitalize`}>{inv.status}</Badge></TableCell>
                  <TableCell className="text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-muted-foreground hover:text-foreground" data-testid={`actions-btn-ap-${inv.id}`}>
                          <MoreHorizontal size={16} />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="bg-popover border-border min-w-[160px]">
                        <DropdownMenuItem onClick={() => openEditDialog(inv)} className="text-foreground gap-2 cursor-pointer" data-testid={`edit-btn-ap-${inv.id}`}>
                          <Pencil size={14} /> Edit
                        </DropdownMenuItem>
                        {inv.uploaded_file ? (
                          <>
                            <DropdownMenuItem onClick={() => previewVendorFile(inv.uploaded_file.id, inv.uploaded_file.filename)} className="text-foreground gap-2 cursor-pointer" data-testid={`preview-file-${inv.id}`}>
                              <Eye size={14} /> View Invoice
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => downloadVendorFile(inv.uploaded_file.id, inv.uploaded_file.filename)} className="text-foreground gap-2 cursor-pointer" data-testid={`download-file-${inv.id}`}>
                              <FileDown size={14} /> Download Invoice
                            </DropdownMenuItem>
                          </>
                        ) : (
                          <>
                            <DropdownMenuItem onClick={() => openPreview(inv.id, inv.invoice_number)} className="text-foreground gap-2 cursor-pointer" data-testid={`preview-btn-${inv.id}`}>
                              <Eye size={14} /> Preview PDF
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => downloadPdf(inv.id, inv.invoice_number)} className="text-foreground gap-2 cursor-pointer" data-testid={`pdf-btn-${inv.id}`}>
                              <FileDown size={14} /> Download PDF
                            </DropdownMenuItem>
                          </>
                        )}
                        {inv.status === 'pending' && (
                          <>
                            <DropdownMenuSeparator className="bg-border" />
                            <DropdownMenuItem onClick={() => updateStatus(inv.id, 'approved')} className="text-emerald-400 gap-2 cursor-pointer" data-testid={`approve-btn-${inv.id}`}>
                              <CheckCircle size={14} /> Approve
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => updateStatus(inv.id, 'rejected')} className="text-red-400 gap-2 cursor-pointer" data-testid={`reject-btn-${inv.id}`}>
                              <XCircle size={14} /> Reject
                            </DropdownMenuItem>
                          </>
                        )}
                        {(inv.status === 'approved' || inv.status === 'pending') && (
                          <>
                            <DropdownMenuSeparator className="bg-border" />
                            <DropdownMenuItem onClick={() => updateStatus(inv.id, 'paid')} className="text-emerald-400 gap-2 cursor-pointer" data-testid={`paid-btn-${inv.id}`}>
                              <DollarSign size={14} /> Mark as Paid
                            </DropdownMenuItem>
                          </>
                        )}
                        <DropdownMenuSeparator className="bg-border" />
                        <DropdownMenuItem onClick={() => deleteInvoice(inv.id)} className="text-red-400 gap-2 cursor-pointer" data-testid={`delete-btn-${inv.id}`}>
                          <Trash2 size={14} /> Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-4xl bg-card border-border max-h-[90vh] overflow-y-auto" data-testid="create-payable-dialog">
          <DialogHeader>
            <DialogTitle className="text-card-foreground">{editId ? 'Edit Vendor Invoice' : 'New Vendor Invoice'}</DialogTitle>
            <DialogDescription className="text-muted-foreground">{editId ? 'Update the vendor invoice details' : 'Enter the vendor invoice details'}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-card-foreground/80">Vendor Name *</Label>
                <Input value={form.vendor_name} onChange={e => setForm({ ...form, vendor_name: e.target.value })}
                  className="bg-[hsl(var(--surface-input))] border-input text-foreground" data-testid="vendor-name-input" placeholder="Vendor name" />
              </div>
              <div className="space-y-2">
                <Label className="text-card-foreground/80">Vendor Email</Label>
                <Input value={form.vendor_email} onChange={e => setForm({ ...form, vendor_email: e.target.value })}
                  className="bg-[hsl(var(--surface-input))] border-input text-foreground" data-testid="vendor-email-input" placeholder="vendor@email.com" />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-card-foreground/80">Billing Address</Label>
              <Input value={form.billing_address} onChange={e => setForm({ ...form, billing_address: e.target.value })}
                className="bg-[hsl(var(--surface-input))] border-input text-foreground" data-testid="billing-address-input" placeholder="Full billing address" />
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label className="text-card-foreground/80">Invoice Date</Label>
                <Input type="date" value={form.invoice_date} onChange={e => setForm({ ...form, invoice_date: e.target.value })}
                  className="bg-[hsl(var(--surface-input))] border-input text-foreground" data-testid="invoice-date-input" />
              </div>
              <div className="space-y-2">
                <Label className="text-card-foreground/80">Credit Terms</Label>
                <Select value={String(form.credit_terms)} onValueChange={handleCreditTerms}>
                  <SelectTrigger className="bg-[hsl(var(--surface-input))] border-input text-foreground" data-testid="credit-terms-select"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent className="bg-popover border-border">
                    {CREDIT_TERMS.map(d => <SelectItem key={d} value={String(d)} className="text-foreground">{d === 0 ? 'Custom' : `Net ${d} days`}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-card-foreground/80">Due Date</Label>
                <Input type="date" value={form.due_date} onChange={e => setForm({ ...form, due_date: e.target.value })}
                  className="bg-[hsl(var(--surface-input))] border-input text-foreground" data-testid="due-date-input" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-card-foreground/80">Currency</Label>
                <Select value={form.currency} onValueChange={v => setForm({ ...form, currency: v })}>
                  <SelectTrigger className="bg-[hsl(var(--surface-input))] border-input text-foreground" data-testid="currency-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-popover border-border">
                    {CURRENCY_LIST.map(c => <SelectItem key={c} value={c} className="text-foreground">{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-card-foreground/80">Description</Label>
                <Input value={form.description} onChange={e => setForm({ ...form, description: e.target.value })}
                  className="bg-[hsl(var(--surface-input))] border-input text-foreground" data-testid="description-input" placeholder="Invoice description" />
              </div>
            </div>

            {/* Line Items with HSN/SAC and Tax */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label className="text-card-foreground/80 text-sm font-medium">Line Items</Label>
                <Button size="sm" variant="outline" onClick={addItem} className="border-input text-card-foreground/80 h-7 text-xs" data-testid="add-item-button">
                  <Plus size={12} className="mr-1" /> Add Item
                </Button>
              </div>
              {/* Table Header */}
              <div className="grid grid-cols-24 gap-2 px-1">
                <div className="col-span-3"><span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">HSN/SAC</span></div>
                <div className="col-span-6"><span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Description</span></div>
                <div className="col-span-2"><span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Quantity</span></div>
                <div className="col-span-3"><span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Rate</span></div>
                <div className="col-span-2"><span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Tax %</span></div>
                <div className="col-span-3"><span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Tax Amt</span></div>
                <div className="col-span-4"><span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Amount</span></div>
                <div className="col-span-1"></div>
              </div>
              <div className="space-y-2">
                {form.items.map((item, idx) => (
                  <div key={idx} className="grid grid-cols-24 gap-2 items-center">
                    <div className="col-span-3">
                      <Input value={item.hsn_sac_code} onChange={e => updateItem(idx, 'hsn_sac_code', e.target.value)}
                        className="bg-[hsl(var(--surface-input))] border-input text-foreground h-9 text-sm" placeholder="Code" data-testid={`item-hsn-${idx}`} />
                    </div>
                    <div className="col-span-6">
                      <Input value={item.description} onChange={e => updateItem(idx, 'description', e.target.value)}
                        className="bg-[hsl(var(--surface-input))] border-input text-foreground h-9 text-sm" placeholder="Item description" data-testid={`item-desc-${idx}`} />
                    </div>
                    <div className="col-span-2">
                      <Input type="number" value={item.quantity} onChange={e => updateItem(idx, 'quantity', e.target.value)}
                        className="bg-[hsl(var(--surface-input))] border-input text-foreground h-9 text-sm text-center" data-testid={`item-qty-${idx}`} />
                    </div>
                    <div className="col-span-3">
                      <Input type="number" value={item.unit_price} onChange={e => updateItem(idx, 'unit_price', e.target.value)}
                        className="bg-[hsl(var(--surface-input))] border-input text-foreground h-9 text-sm" placeholder="0.00" data-testid={`item-price-${idx}`} />
                    </div>
                    <div className="col-span-2">
                      <Input type="number" value={item.tax_rate} onChange={e => updateItem(idx, 'tax_rate', e.target.value)}
                        className="bg-[hsl(var(--surface-input))] border-input text-foreground h-9 text-sm text-center" placeholder="0" data-testid={`item-tax-${idx}`} />
                    </div>
                    <div className="col-span-3">
                      <div className="h-9 flex items-center text-sm text-muted-foreground font-mono px-2 bg-muted/30 rounded-md border border-transparent">{formatCurrency(item.tax_amount, form.currency)}</div>
                    </div>
                    <div className="col-span-4">
                      <div className="h-9 flex items-center text-sm text-foreground font-mono font-medium px-2 bg-muted/30 rounded-md border border-transparent">{formatCurrency(item.amount, form.currency)}</div>
                    </div>
                    <div className="col-span-1 flex justify-center">
                      {form.items.length > 1 && (
                        <Button size="sm" variant="ghost" className="text-red-400 h-8 w-8 p-0" onClick={() => removeItem(idx)} title="Remove Item"><Trash2 size={14} /></Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
            {/* Totals */}
            <div className="flex flex-col items-end gap-1 pt-2 border-t border-border">
              <div className="text-sm text-muted-foreground" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Subtotal: {formatCurrency(form.subtotal, form.currency)}</div>
              <div className="text-sm text-muted-foreground" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Tax: {formatCurrency(form.tax_amount, form.currency)}</div>
              <div className="text-lg font-bold text-card-foreground" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                Total: {formatCurrency(form.amount, form.currency)}
              </div>
            </div>
            {/* Bank Details & Notes */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-card-foreground/80">Bank Details</Label>
                <textarea value={form.bank_details} onChange={e => setForm({ ...form, bank_details: e.target.value })}
                  className="w-full bg-[hsl(var(--surface-input))] border border-input text-card-foreground rounded-md px-3 py-2 text-sm min-h-[60px] focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none"
                  data-testid="bank-details-input" placeholder="Bank name, account number, IFSC..." />
              </div>
              <div className="space-y-2">
                <Label className="text-card-foreground/80">Notes</Label>
                <textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })}
                  className="w-full bg-[hsl(var(--surface-input))] border border-input text-card-foreground rounded-md px-3 py-2 text-sm min-h-[60px] focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none"
                  data-testid="notes-input" placeholder="Additional notes for the invoice" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="border-input text-card-foreground/80" data-testid="cancel-button">Cancel</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="save-payable-button">
              {saving ? <Loader2 className="animate-spin" size={16} /> : (editId ? 'Update Invoice' : 'Create Invoice')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* PDF Preview */}
      <Dialog open={previewOpen} onOpenChange={(open) => { if (!open && previewUrl) { URL.revokeObjectURL(previewUrl); setPreviewUrl(null); } setPreviewOpen(open); }}>
        <DialogContent className="max-w-4xl bg-card border-border h-[90vh] flex flex-col" data-testid="preview-dialog-ap">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle className="text-card-foreground flex items-center gap-2"><Eye size={18} /> Invoice Preview — {previewNumber}</DialogTitle>
            <DialogDescription className="text-muted-foreground">Preview the invoice before downloading</DialogDescription>
          </DialogHeader>
          <div className="flex-1 min-h-0 rounded-md overflow-hidden border border-border bg-white">
            {previewUrl && <iframe src={previewUrl + "#toolbar=1&navpanes=0"} className="w-full h-full" title="PDF Preview" style={{border:"none"}} />}
          </div>
          <DialogFooter className="flex-shrink-0">
            <Button variant="outline" onClick={() => setPreviewOpen(false)} className="border-input text-card-foreground/80">Close</Button>
            <Button onClick={() => { if (previewUrl) { const a = document.createElement('a'); a.href = previewUrl; a.download = `${previewNumber}.pdf`; a.click(); } }} className="bg-blue-600 hover:bg-blue-700 text-white gap-2"><FileDown size={14} /> Download PDF</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
